<template>
  <div class="modal-wrapper" :class="{ 'd-none': !isVisible }">
    <div :id="modalId" class="modal fade" tabindex="-1" role="dialog" aria-labelledby="modalLabel" aria-hidden="true">
      <div class="rp-password modal-dialog modal-dialog-centered" role="document">
        <div class="modal-content">
          <div class="modal-header justify-content-center">
            <h5 class="fw-bold modal-title text-center" id="modalLabel">{{ title }}</h5>
          </div>

          <!-- Dynamic Content -->
          <div v-if="!loading" class="modal-body text-center">
            <span v-if="message" v-html="message" class="modal-message"></span>
            <textarea
              v-if="options.hasForm"
              v-model="formContent"
              :placeholder="options.formInputs?.placeholder || ''"
              class="form-control mt-3"
              rows="4"
              ref="textarea"
            ></textarea>
          </div>

          <!-- Dynamic Footer -->
          <div class="modal-footer justify-content-center">
            <button 
              v-for="(button, index) in computedActions" 
              :key="index" 
              @click="buttonClicked(button)"
              :class="[
                'btn', 
                'btn_norn', 
                'modal-btn', 
                computedActions.length > 1 && index === computedActions.length - 1 ? 'warning' : (button.type || 'btn-primary')
              ]"
              :disabled="button.disabled || (options.hasForm && options.formInputs?.allowReasonSubmit === false)"
              ref="button"
            >
              {{ button.text }}
            </button>
          </div>
        </div>
      </div>
    </div>
    <div class="modal-backdrop fade" :class="{ 'show': isVisible }"></div>
  </div>
</template>

<script>
import { Modal } from "bootstrap";
import { eventBus } from '@/utils/eventBus';
import { MODAL_EVENTS } from '@/utils/modalUtils';

export default {
  name: "GlobalModal",
  data() {
    return {
      isVisible: false,
      modalId: '',
      title: '',
      message: '',
      actions: [],
      loading: false,
      options: {},
      formContent: '',
      modalInstance: null
    };
  },
  computed: {
    computedActions() {
      return this.actions.map(action => ({
        ...action,
        disabled: this.options.hasForm && !this.formContent && this.options.formInputs?.allowReasonSubmit === false
      }));
    }
  },
  methods: {
    showModal(config) {
      this.modalId = config.modalId;
      this.title = config.title;
      this.message = config.message;
      this.actions = config.actions;
      this.loading = config.loading;
      this.options = config.options || {};
      this.formContent = '';
      
      this.$nextTick(() => {
        const modalElement = document.getElementById(this.modalId);
        if (modalElement && !this.modalInstance) {
          this.modalInstance = new Modal(modalElement, {
            backdrop: 'static',
            keyboard: false
          });
        }
        this.isVisible = true;
        if (this.modalInstance) {
          this.modalInstance.show();
        }

        // Focus on textarea if it exists
        if (this.options.hasForm && this.$refs.textarea) {
          this.$refs.textarea.focus();
        }
      });
    },

    hideModal() {
      if (this.modalInstance) {
        this.modalInstance.hide();
      }
      this.isVisible = false;
      this.resetModal();
    },

    resetModal() {
      this.title = '';
      this.message = '';
      this.actions = [];
      this.loading = false;
      this.options = {};
      this.formContent = '';
    },

    buttonClicked(button) {
      if (!button.action || button.action === 'close') {
        this.hideModal();
      } else if (typeof button.action === 'function') {
        // Pass form content if modal has a form
        if (this.options.hasForm) {
          button.action(this.formContent);
        } else {
          button.action();
        }
        if (button.dismiss !== false) {
          this.hideModal();
        }
      }
    }
  },
  mounted() {
    // Create bound event handlers
    this.modalShowHandler = this.showModal.bind(this);
    this.modalHideHandler = this.hideModal.bind(this);

    // Listen for modal events
    eventBus.on(MODAL_EVENTS.SHOW, this.modalShowHandler);
    eventBus.on(MODAL_EVENTS.HIDE, this.modalHideHandler);
  },
  beforeUnmount() {
    // Clean up event listeners
    eventBus.off(MODAL_EVENTS.SHOW, this.modalShowHandler);
    eventBus.off(MODAL_EVENTS.HIDE, this.modalHideHandler);
  }
};
</script>

<style scoped>
.modal-content {
  background-color: #e4dcd3;
  position: relative;
  z-index: 1056;
}

.modal-header {
  padding: 1rem;
  text-align: center;
  border-bottom: none;
}

.modal-title {
  font-size: 24px;
  text-align: center;
  margin: 0;
  width: 100%;
}

.modal-body {
  padding: 1rem;
  text-align: center;
}

.modal-message {
  font-size: 16px;
  line-height: 1.5;
  margin: 0;
}

button {
  border-radius: 5px;
  font-weight: 500;
  font-size: 16px;
  color: #ffffff;
  background: #002c5f;
  border: solid #002856 1px;
  margin: 0 10px;
  width: 200px;
  width: 12rem !important;
}

button:hover {
  background: #00AAD2;
  border: solid #002856 1px;
}

button:focus {
  box-shadow: none;
  outline: none;
}

button.warning {
  background: rgb(239, 65, 35);
  border: solid rgb(239, 65, 35) 1px;
}

.modal-footer {
  padding: 1rem;
  text-align: center;
  border-top: none;
}

textarea {
  resize: vertical;
  min-height: 100px;
}

.modal-backdrop {
  opacity: 0.5;
  z-index: 1055;
}

.modal-wrapper {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 1055;
}

.modal {
  z-index: 1056;
}

.modal-dialog {
  z-index: 1056;
  position: relative;
}

.d-none {
  display: none !important;
}
</style>